import { createFileRoute } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ | Nkadimeng Danny Legal & Compliance Support" },
      {
        name: "description",
        content:
          "Frequently asked questions about our legal and compliance services, consultations, fees and the CCMA process.",
      },
      { property: "og:title", content: "Frequently Asked Questions" },
      {
        property: "og:description",
        content:
          "Answers to common questions about consultations, fees, CCMA matters and more.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/faq" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FAQPage,
});

const faqs = [
  {
    q: "How do consultations work?",
    a: "You can book a physical, telephone, Microsoft Teams or Zoom consultation. Once you submit your booking, we'll confirm by email with the meeting details and any preparation steps.",
  },
  {
    q: "Are you an attorney?",
    a: "No. Nkadimeng Danny is an LLB graduate from UNISA and is not an admitted attorney. We provide legal support, paralegal assistance, drafting, compliance and CCMA case preparation — we do not represent clients at the CCMA, bargaining councils or in court. Where a matter requires an admitted practitioner we refer or collaborate.",
  },
  {
    q: "How much do you charge?",
    a: "Fees depend on the service and complexity of your matter. We aim to be affordable and transparent — request a quotation via the contact form and we'll respond promptly.",
  },
  {
    q: "Can you help with CCMA matters?",
    a: "Yes — with preparation only. We assist with referrals (LRA 7.11 / 7.12), case strategy, bundles, witness statements and coaching so you walk in ready. We do not appear or represent you at conciliation or arbitration; if representation is needed, we refer to an admitted attorney.",
  },
  {
    q: "What can an LLB graduate actually do for me?",
    a: "Plenty. An LLB graduate has completed the same legal degree as admitted attorneys and can: research and explain the law in plain English, draft contracts, policies, letters of demand, affidavits and POPIA documents, prepare CCMA referrals and case bundles, coach witnesses, run compliance audits for small businesses, assist with debt negotiation, and provide Court Online / CaseLines filing support. The reserved work — appearing in court, conveyancing, notarial work and giving formal legal advice — stays with admitted attorneys, and we refer those parts of your matter on.",
  },
  {
    q: "Do you serve clients outside Gauteng?",
    a: "Yes. We serve clients throughout South Africa using telephone, MS Teams and Zoom consultations.",
  },
  {
    q: "How do you handle confidentiality?",
    a: "All client information is handled with strict confidentiality and in line with POPIA. We never share your information without your consent.",
  },
];

function FAQPage() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <h1 className="font-display text-4xl text-navy md:text-5xl">
            Frequently asked questions
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Quick answers to common questions about our services and how we
            work.
          </p>
        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="mx-auto max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-display text-lg text-navy">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
